import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';
import { menuAPI, tenantAPI, cashierAPI } from '../lib/api';
import { setAppBusy } from '../lib/appVersion';
import { errorMessage } from '../lib/errorMessage';
import { toast } from 'sonner';
import { 
  Search, Plus, Minus, CreditCard, Clock, Users, Percent, User, Receipt, 
  LayoutGrid, Table2, ArrowDownToLine, ArrowUpFromLine, Banknote, Lock, 
  AlertTriangle, DollarSign, RefreshCw, LayoutDashboard, ChefHat, Boxes,
  Heart, TrendingUp, ClipboardList, Store, LogOut, X, Monitor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import TableMapPage from './pos/TableMapPage';
import TicketPanel from './pos/TicketPanel';
import PaymentModal from './pos/PaymentModal';
import VoidModal from './pos/VoidModal';
import ShiftModal from './pos/ShiftModal';
import ManagerPinDialog from '../components/ManagerPinDialog';
import CashMovementDialog from '../components/CashMovementDialog';
import ZReadingDialog from '../components/ZReadingDialog';
import { SyncStatusIndicator } from '../components/SyncStatusIndicator';
import { SendToKitchenButton, ReceiptPrintDialog, VoidSlipPrintDialog } from '../components/PrintDialogs';
import { UserMenu } from '../components/UserMenu';
import { BrandWordmark } from '../components/BrandLogo';

// Navigation Drawer Component for POS
function POSNavDrawer({ isOwner, isManager, onClose }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const NavItem = ({ to, icon: Icon, label, highlight }) => (
    <button
      onClick={() => handleNavigate(to)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
        highlight 
          ? 'bg-secondary/15 text-secondary' 
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </button>
  );
  
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="nav-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <nav className="relative w-80 bg-card border-r border-border flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <span className="font-heading font-extrabold text-foreground text-2xl tracking-tight">BEN<span className="text-secondary">TA</span></span>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <ScrollArea className="flex-1 py-4 px-3">
          <div className="space-y-1">
            <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
            <NavItem to="/pos" icon={CreditCard} label="Point of Sale" highlight />
            <NavItem to="/kds" icon={ChefHat} label="Kitchen Display" />
            
            {(isOwner || isManager) && (
              <>
                <div className="pt-6 pb-2 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Management
                </div>
                <NavItem to="/backoffice/inventory" icon={Boxes} label="Inventory" />
                <NavItem to="/backoffice/customers" icon={Heart} label="Customers / CRM" />
                <NavItem to="/finance/reports" icon={TrendingUp} label="Reports" />
                <NavItem to="/finance/audit" icon={ClipboardList} label="Audit Logs" />
              </>
            )}
            
            <div className="pt-6 pb-2 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Account
            </div>
            <NavItem to="/select-outlet" icon={Store} label="Switch Outlet" />
          </div>
        </ScrollArea>
        
        <div className="p-4 border-t border-border">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
            data-testid="nav-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export default function POSPage() {
  const navigate = useNavigate();
  const { user, currentOutlet, token, isOwner, isManager, logout } = useAuth();
  const { isOnline, getCachedMenu, menuCache, triggerSync } = useOffline();
  const selectedOutlet = currentOutlet?.id;
  const [showCart, setShowCart] = useState(false); // phones: the order is a full-screen sheet
  const [viewMode, setViewMode] = useState('pos'); // 'pos' | 'tables'
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [serviceType, setServiceType] = useState('dine_in');
  const [showCustomItem, setShowCustomItem] = useState(false);
  const [customItem, setCustomItem] = useState({ name: '', unit_price: '', quantity: 1, tax_type: 'VAT', manager_pin: '' });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);

  // An order on screen is unsaved work belonging to a customer at the
  // counter, so the update banner must not reload the page over it.
  useEffect(() => {
    setAppBusy(cart.length > 0);
    return () => setAppBusy(false);
  }, [cart.length]);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [shift, setShift] = useState(null);
  const [outlet, setOutlet] = useState(null);
  
  // Navigation drawer
  const [showNavMenu, setShowNavMenu] = useState(false);
  
  // Day status and cash tracking
  const [dayStatus, setDayStatus] = useState({ is_locked: false, business_date: null });
  const [cashBalance, setCashBalance] = useState(0);
  const [loadingDayStatus, setLoadingDayStatus] = useState(true);
  
  // Modals
  const [showPayment, setShowPayment] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidSlipId, setVoidSlipId] = useState(null);  // prints the BIR void document
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [showShiftClose, setShowShiftClose] = useState(false);
  
  // Cash Movement & Z-Reading dialogs
  const [showCashIn, setShowCashIn] = useState(false);
  const [showCashOut, setShowCashOut] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showZReading, setShowZReading] = useState(false);
  const [cashMovementLoading, setCashMovementLoading] = useState(false);
  const [zReadingLoading, setZReadingLoading] = useState(false);
  const [zReadingResult, setZReadingResult] = useState(null);
  const [shiftSummary, setShiftSummary] = useState(null);
  
  // Manager PIN dialog for refunds
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);

  // BIR-grade discount state
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountType, setDiscountType] = useState('MANUAL_PERCENT');
  const [discountValue, setDiscountValue] = useState('');
  const [discountBeneficiaryName, setDiscountBeneficiaryName] = useState('');
  const [discountBeneficiaryId, setDiscountBeneficiaryId] = useState('');
  const [discountReference, setDiscountReference] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountManagerPin, setDiscountManagerPin] = useState('');
  // Statutory discounts (Senior, PWD, National Athlete, Solo Parent) come from
  // the SERVER registry rather than a hardcoded list here, so a change in the
  // law does not need a matching edit in this file, and the rate the cashier
  // is shown is the one the fiscal engine will actually apply.
  const [statutoryTypes, setStatutoryTypes] = useState([]);
  // Custom (cashier-defined) discounts, e.g. "Athlete", "Single Mother"
  const [customDiscounts, setCustomDiscounts] = useState([]);
  const [customDiscName, setCustomDiscName] = useState('');
  const [customDiscMode, setCustomDiscMode] = useState('percent');
  const isCustomDiscount = discountType === 'CUSTOM_NEW' || discountType.startsWith('SAVED::');
  const statutoryEntry = statutoryTypes.find((t) => t.code === discountType) || null;
  const isStatutory = !!statutoryEntry;
  const loadStatutoryTypes = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/bir/statutory-discounts`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setStatutoryTypes(await r.json());
    } catch { /* noop */ }
  }, [token]);
  const loadCustomDiscounts = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/bir/custom-discounts`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setCustomDiscounts(await r.json());
    } catch { /* noop */ }
  }, [token]);
  // Group-bill SC/PWD: which lines the beneficiary consumed, or a per-head split
  const [discountBasis, setDiscountBasis] = useState('items'); // 'items' | 'share' | 'whole'
  const [discountEligibleIds, setDiscountEligibleIds] = useState([]);
  const [discountGroupSize, setDiscountGroupSize] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);

  // Per-item (line) discount state
  const [showItemDiscount, setShowItemDiscount] = useState(false);
  const [itemDiscTarget, setItemDiscTarget] = useState(null);
  const [itemDiscType, setItemDiscType] = useState('percent');
  const [itemDiscValue, setItemDiscValue] = useState('');
  const [itemDiscPin, setItemDiscPin] = useState('');
  const [itemDiscBusy, setItemDiscBusy] = useState(false);

  // Customer state  
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // Fetch day status and cash balance
  const fetchDayStatus = useCallback(async () => {
    if (!selectedOutlet || !token) return;
    setLoadingDayStatus(true);
    try {
      if (isOnline) {
        const [statusRes, balanceRes] = await Promise.all([
          cashierAPI.getDayStatus(selectedOutlet),
          cashierAPI.getCashBalance(selectedOutlet)
        ]);
        setDayStatus(statusRes.data);
        setCashBalance(balanceRes.data.balance);
      } else {
        // Offline - use cached status or assume not locked
        setDayStatus({ is_locked: false, business_date: new Date().toISOString().split('T')[0] });
        setCashBalance(0);
      }
    } catch (err) {
      console.error('Failed to fetch day status:', err);
      // Fallback to offline mode
      setDayStatus({ is_locked: false, business_date: new Date().toISOString().split('T')[0] });
    }
    setLoadingDayStatus(false);
  }, [selectedOutlet, token, isOnline]);

  const fetchData = useCallback(async () => {
    if (!selectedOutlet || !token) return;
    try {
      if (isOnline) {
        // Online mode - fetch from server
        const [catRes, outletRes, shiftRes] = await Promise.all([
          menuAPI.getCategories(),
          tenantAPI.getOutlets(),
          fetch(`${API_URL}/api/pos/shifts/current?outlet_id=${selectedOutlet}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json())
        ]);
        
        setCategories(catRes.data);
        const currentOutlet = outletRes.data.find(o => o.id === selectedOutlet);
        setOutlet(currentOutlet);
        setShift(shiftRes);
        
        // Cache menu data for offline use
        if (menuCache) {
          await menuCache.cacheCategories(catRes.data);
        }
        
        if (catRes.data.length > 0) {
          setSelectedCategory(catRes.data[0].id);
        }
      } else {
        // Offline mode - use cached data
        const cached = await getCachedMenu();
        if (cached.categories.length > 0) {
          setCategories(cached.categories);
          setSelectedCategory(cached.categories[0].id);
          toast.info('Using cached menu data (offline mode)');
        } else {
          toast.warning('No cached menu data available');
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      // Try offline fallback
      try {
        const cached = await getCachedMenu();
        if (cached.categories.length > 0) {
          setCategories(cached.categories);
          setSelectedCategory(cached.categories[0].id);
          toast.info('Using cached menu data');
        }
      } catch (cacheErr) {
        console.error('Failed to load cached data:', cacheErr);
      }
    }
  }, [selectedOutlet, token, isOnline, getCachedMenu, menuCache]);

  useEffect(() => {
    fetchData();
    fetchDayStatus();
  }, [fetchData, fetchDayStatus]);

  useEffect(() => {
    if (!selectedCategory || !token) return;
    menuAPI.getItems(selectedCategory).then(res => setMenuItems(res.data)).catch(console.error);
  }, [selectedCategory, token]);

  // Fetch shift summary when shift changes
  useEffect(() => {
    if (shift && selectedOutlet && token) {
      cashierAPI.getShiftSummary(selectedOutlet, shift.id)
        .then(res => setShiftSummary(res.data))
        .catch(console.error);
    }
  }, [shift, selectedOutlet, token]);

  const filteredItems = menuItems.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const addToCart = async (item) => {
    if (dayStatus.is_locked) {
      toast.error('Cannot add items - Day is locked by Z-Reading');
      return;
    }
    
    // Update local cart state immediately for UI responsiveness
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1, notes: '' }];
    });

    try {
      // Ensure a ticket exists. For quick-service (no table selected) there is no
      // ticket yet, so create one — otherwise currentTicket.id stays null and the
      // "Send to Kitchen" / "Pay" buttons remain permanently disabled.
      let ticket = currentTicket;
      if (!ticket?.id) {
        const outletId = currentOutlet?.id || outlet?.id || selectedOutlet || localStorage.getItem('selectedOutletId');
        if (!outletId) {
          toast.error('No outlet selected');
          return;
        }
        const createRes = await fetch(`${API_URL}/api/pos/tickets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            outlet_id: outletId,
            table_id: selectedTable?.id || null,
            guest_count: 1,
            service_type: selectedTable?.id ? 'dine_in' : serviceType
          })
        });
        if (!createRes.ok) {
          const err = await createRes.json().catch(() => ({}));
          toast.error(err.detail || 'Failed to start order');
          return;
        }
        ticket = await createRes.json();
        setCurrentTicket(ticket);
      }

      // Persist the item to the ticket
      const res = await fetch(`${API_URL}/api/pos/tickets/${ticket.id}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ menu_item_id: item.id, quantity: 1, notes: '' })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add item to order');
        return;
      }

      // Refresh authoritative ticket state (totals + items) so action buttons enable
      const tRes = await fetch(`${API_URL}/api/pos/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (tRes.ok) {
        const fresh = await tRes.json();
        setCurrentTicket(fresh);
        setCart(fresh.items?.filter(i => !i.is_voided).map(i => ({
          id: i.menu_item_id || i.id, name: i.name, price: i.unit_price,
          quantity: i.quantity, notes: i.notes, orderItemId: i.id, discount_amount: i.discount_amount || 0
        })) || []);
      }
      toast.success(`Added ${item.name}`);
    } catch (err) {
      console.error('Failed to add item to ticket:', err);
      toast.error('Failed to sync item to order');
    }
  };

  const handleBarcodeScan = async (e) => {
    if (e.key !== 'Enter') return;
    const code = barcodeInput.trim();
    if (!code) return;
    setBarcodeInput('');
    // Try the locally-loaded items first (current category), else ask the backend.
    const local = menuItems.find(i => i.barcode === code || i.sku === code);
    if (local) { addToCart(local); return; }
    try {
      const res = await fetch(`${API_URL}/api/menu/items/by-barcode/${encodeURIComponent(code)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('No product matches that barcode'); return; }
      addToCart(await res.json());
    } catch (err) {
      toast.error('Barcode lookup failed');
    }
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => {
      const updated = prev.map(item => 
        item.id === itemId ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
      ).filter(item => item.quantity > 0);
      return updated;
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity - (item.discount_amount || 0)), 0);
  // Service charge applies to dine-in only; take-out/delivery get none (client rule, 2026-09-28).
  const effectiveServiceType = currentTicket?.service_type || (selectedTable?.id ? 'dine_in' : serviceType);
  const serviceChargeRate = effectiveServiceType === 'dine_in' ? (outlet?.service_charge_rate ?? 10) : 0;
  const vatRate = outlet?.vat_rate || 12;
  
  // Calculate discount. `appliedDiscount.value` is already the amount the
  // backend booked (setAppliedDiscount stores data.discount_amount), so the
  // 'percent' arm below is dead — apiDiscountType is 'MANUAL'/'SENIOR'/…,
  // never the literal 'percent'. Left as-is rather than widened: the ticket
  // is the source of truth whenever it is in sync.
  const localDiscountAmount = appliedDiscount 
    ? (appliedDiscount.type === 'percent' 
        ? subtotal * (appliedDiscount.value / 100) 
        : appliedDiscount.value)
    : 0;
  // THE BACKEND IS THE ENGINE FOR TICKET MONEY (backend/services/tax_service.py).
  // It computes a percentage discount over subtotal + service charge, and for a
  // statutory claim it also removes VAT from the base — neither rule can be
  // re-derived from the cart alone. Re-deriving them here put a SECOND, different
  // Total on the same screen: the service charge was taken on the discounted
  // subtotal while `Pay` showed the backend's figure taken on the gross, so a
  // discounted bill read Total ₱629.63 beside Pay ₱633.15 (they differ by
  // rate × discount). Display what will actually be charged.
  //
  // `ticket.discount_amount` already holds the statutory total when claims exist
  // (pos_v2.recalculate_ticket), so it is the whole discount either way — never
  // add sc_pwd_discount to it or a senior bill double-counts.
  const ticketSynced = !!currentTicket
    && Math.abs(Number(currentTicket.subtotal ?? NaN) - subtotal) < 0.005;

  // The discount LINE must come from the same place as the Total, or the
  // summary shows figures that do not add up to the number beneath them.
  const discountAmount = ticketSynced
    ? Number(currentTicket.discount_amount || 0)
    : localDiscountAmount;

  // Before the ticket exists, and for the moment an edit is still in flight, fall
  // back to a local estimate that follows the SAME rules, so the figures do not
  // jump between two answers. With no discount the two agree exactly.
  const serviceCharge = ticketSynced
    ? Number(currentTicket.service_charge || 0)
    : subtotal * (serviceChargeRate / 100);
  // VAT (inclusive) is extracted from the items only — the service charge is NOT taxable.
  const vatAmount = ticketSynced
    ? Number(currentTicket.tax_amount || 0)
    // A point-of-sale discount reduces the VAT base (RR 16-2005 sec 4.106-9),
    // so the estimate nets it off too rather than taxing the gross.
    : Math.max(0, subtotal - discountAmount) * (vatRate / 100) / (1 + vatRate / 100);
  const total = ticketSynced
    ? Number(currentTicket.total || 0)
    : subtotal + serviceCharge - discountAmount;

  // ---- Customer screen: push exactly what the cart summary shows to /display/<token>.
  // lastDisplay holds the last order snapshot so that, when payment clears the cart, the customer's
  // screen can say "Thank you, paid X" for a few seconds instead of snapping straight to the welcome.
  const lastDisplay = useRef(null);
  const paidHold = useRef(0);
  const pushDisplay = useCallback((snapshot) => {
    if (!token || !selectedOutlet) return;
    fetch(`${API_URL}/api/display/push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ outlet_id: selectedOutlet, counter: '1', snapshot }),
      keepalive: true,
    }).catch(() => {});
  }, [token, selectedOutlet]);

  useEffect(() => {
    const idle = { status: 'idle', outlet_name: outlet?.name || null, items: [] };
    if (cart.length === 0) {
      const wait = paidHold.current - Date.now();
      if (wait > 0) {
        const t = setTimeout(() => pushDisplay(idle), wait + 50);
        return () => clearTimeout(t);
      }
      lastDisplay.current = null;
      pushDisplay(idle);
      return undefined;
    }
    const snapshot = {
      status: 'order',
      outlet_name: outlet?.name || null,
      ticket_number: currentTicket?.ticket_number || null,
      service_type: effectiveServiceType || null,
      items: cart.map(i => ({
        name: String(i.name || '').slice(0, 120),
        quantity: Number(i.quantity || 0),
        unit_price: Number(i.price || 0),
        line_total: Number(i.price * i.quantity - (i.discount_amount || 0)),
        discount: Number(i.discount_amount || 0),
      })),
      subtotal: Number(subtotal), discount: Number(discountAmount),
      service_charge: Number(serviceCharge), vat: Number(vatAmount), total: Number(total),
    };
    lastDisplay.current = snapshot;
    const t = setTimeout(() => pushDisplay(snapshot), 250);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, currentTicket, appliedDiscount, subtotal, discountAmount, serviceCharge, vatAmount, total, effectiveServiceType, pushDisplay]);

  const openCustomerScreen = async () => {
    try {
      const r = await fetch(`${API_URL}/api/display/link?outlet_id=${encodeURIComponent(selectedOutlet)}&counter=1`,
        { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'failed');
      const url = `${window.location.origin}/display/${d.token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard can be blocked; the link is shown anyway */ }
      toast.success('Customer screen link copied', {
        description: url,
        duration: 12000,
        action: { label: 'Open', onClick: () => window.open(url, '_blank') },
      });
    } catch (e) {
      toast.error('Could not get the customer screen link');
    }
  };

  const handleOpenShift = async (startingCash) => {
    if (dayStatus.is_locked) {
      toast.error('Cannot open shift - Day is locked by Z-Reading');
      setShowShiftModal(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/pos/shifts/open?outlet_id=${selectedOutlet}&starting_cash=${startingCash}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setShift(data);
        toast.success('Shift opened');
        setShowShiftModal(false);
        fetchDayStatus();
      } else {
        toast.error(data.detail || 'Failed to open shift');
      }
    } catch (err) {
      toast.error('Failed to open shift');
    }
  };

  const handleVoidItem = (item) => {
    if (dayStatus.is_locked) {
      toast.error('Cannot void - Day is locked by Z-Reading');
      return;
    }
    setVoidTarget({ type: 'item', item, ticketId: currentTicket?.id });
    setShowVoid(true);
  };

  const handleVoidTicket = () => {
    if (!currentTicket) return;
    if (dayStatus.is_locked) {
      toast.error('Cannot void - Day is locked by Z-Reading');
      return;
    }
    setVoidTarget({ type: 'ticket', ticket: currentTicket });
    setShowVoid(true);
  };

  const handlePaymentComplete = (paidTicketId) => {
    if (lastDisplay.current) {
      paidHold.current = Date.now() + 10000;
      pushDisplay({ ...lastDisplay.current, status: 'paid' });
    }
    setCart([]);
    setCurrentTicket(null);
    setSelectedTable(null);
    // The discount belongs to the ticket that was just paid. Left set, it kept
    // showing over the empty order and then over the NEXT customer's bill.
    setAppliedDiscount(null);
    setShowPayment(false);
    toast.success('Payment completed');
    fetchData();
    fetchDayStatus();
    // Offer to print the official receipt right after payment
    if (paidTicketId) handlePrintReceipt(paidTicketId);
  };

  // Guest count prompt state
  const [showGuestCount, setShowGuestCount] = useState(false);
  const [pendingTable, setPendingTable] = useState(null);
  const [guestCount, setGuestCount] = useState(2);

  const handleTableSelect = async (table) => {
    setSelectedTable(table);
    
    if (table.current_ticket_id) {
      // Load existing ticket
      try {
        const res = await fetch(`${API_URL}/api/pos/tickets/${table.current_ticket_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const ticket = await res.json();
        setCurrentTicket(ticket);
        setCart(ticket.items?.filter(i => !i.is_voided).map(i => ({
          id: i.menu_item_id || i.id, name: i.name, price: i.unit_price, quantity: i.quantity, notes: i.notes, orderItemId: i.id, discount_amount: i.discount_amount || 0
        })) || []);
      } catch (err) {
        console.error('Failed to load ticket:', err);
        toast.error('Failed to load ticket');
      }
      setViewMode('pos');
    } else if (table.status === 'available') {
      // Show guest count prompt for available tables
      if (dayStatus.is_locked) {
        toast.error('Day is locked. Cannot seat new guests.');
        setCart([]);
        setCurrentTicket(null);
        setAppliedDiscount(null);
        setViewMode('pos');
        return;
      }
      
      if (!shift) {
        toast.error('Please open a shift first');
        setCart([]);
        setCurrentTicket(null);
        setAppliedDiscount(null);
        setViewMode('pos');
        return;
      }
      
      // Show guest count prompt
      setPendingTable(table);
      setGuestCount(2);
      setShowGuestCount(true);
    } else {
      setCart([]);
      setCurrentTicket(null);
      setAppliedDiscount(null);
      setViewMode('pos');
    }
  };

  // Confirm seating after guest count prompt
  const confirmSeating = async () => {
    if (!pendingTable) return;
    
    try {
      const seatRes = await fetch(`${API_URL}/api/tables/${pendingTable.id}/seat?guest_count=${guestCount}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!seatRes.ok) {
        const err = await seatRes.json();
        throw new Error(err.detail || 'Failed to seat guests');
      }
      
      const seatData = await seatRes.json();
      toast.success(`Seated ${guestCount} guest${guestCount > 1 ? 's' : ''} at ${pendingTable.name} - Ticket #${seatData.ticket_number}`);
      
      // Load the new ticket
      const ticketRes = await fetch(`${API_URL}/api/pos/tickets/${seatData.ticket_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ticket = await ticketRes.json();
      setCurrentTicket(ticket);
      setCart([]);
      setViewMode('pos');
    } catch (err) {
      console.error('Failed to seat guests:', err);
      toast.error(err.message || 'Failed to seat guests');
      setCart([]);
      setCurrentTicket(null);
    }
    
    setShowGuestCount(false);
    setPendingTable(null);
  };

  // Cash Movement Handlers
  const handleCashIn = async (data) => {
    setCashMovementLoading(true);
    try {
      const res = await cashierAPI.cashIn(selectedOutlet, data.amount, data.reason, data.managerPin);
      toast.success(`Cash in successful. New balance: ₱${res.data.new_balance.toFixed(2)}`);
      setShowCashIn(false);
      fetchDayStatus();
    } catch (err) {
      const message = err.response?.data?.detail || 'Cash in failed';
      toast.error(message);
    }
    setCashMovementLoading(false);
  };

  const handleCashOut = async (data) => {
    setCashMovementLoading(true);
    try {
      const res = await cashierAPI.cashOut(selectedOutlet, data.amount, data.reason, data.managerPin);
      toast.success(`Cash out successful. New balance: ₱${res.data.new_balance.toFixed(2)}`);
      setShowCashOut(false);
      fetchDayStatus();
    } catch (err) {
      const message = err.response?.data?.detail || 'Cash out failed';
      toast.error(message);
    }
    setCashMovementLoading(false);
  };

  const handleDeposit = async (data) => {
    setCashMovementLoading(true);
    try {
      const res = await cashierAPI.deposit(selectedOutlet, data.amount, data.bankName, data.bankReference, data.reason, data.managerPin);
      toast.success(`Deposit recorded. New balance: ₱${res.data.new_balance.toFixed(2)}`);
      setShowDeposit(false);
      fetchDayStatus();
    } catch (err) {
      const message = err.response?.data?.detail || 'Deposit failed';
      toast.error(message);
    }
    setCashMovementLoading(false);
  };

  // Z-Reading Handler
  const handleZReading = async (data) => {
    setZReadingLoading(true);
    try {
      const idempotencyKey = `z-${selectedOutlet}-${Date.now()}`;
      const res = await cashierAPI.createZReading(selectedOutlet, data.countedCash, data.notes, idempotencyKey);
      setZReadingResult({
        z_number: res.data.z_number,
        business_date: res.data.business_date,
        totals: res.data.totals,
        hash: res.data.hash
      });
      toast.success('Z-Reading created - Day is now locked');
      fetchDayStatus();
      setShift(null);
    } catch (err) {
      // Never pass a raw validation-error object to toast — it crashes React (#31).
      toast.error(errorMessage(err, 'Failed to create Z-Reading'));
    }
    setZReadingLoading(false);
  };

  const openZReadingDialog = async () => {
    if (shift) {
      // Fetch latest shift summary before showing Z-Reading dialog
      try {
        const res = await cashierAPI.getShiftSummary(selectedOutlet, shift.id);
        setShiftSummary(res.data);
      } catch (err) {
        console.error('Failed to fetch shift summary:', err);
      }
    } else {
      // No shift open: the Z-Reading covers the whole business day, so a
      // leftover summary from an earlier shift must not prefill counted cash.
      setShiftSummary(null);
    }
    setZReadingResult(null);
    setShowZReading(true);
  };

  // Refund handler
  const handleRefund = async (data) => {
    if (!refundTarget) return;
    setRefundLoading(true);
    try {
      const idempotencyKey = `refund-${refundTarget.ticketId}-${Date.now()}`;
      const res = await fetch(`${API_URL}/api/pos/tickets/${refundTarget.ticketId}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          amount: refundTarget.amount,
          reason: data.reason,
          manager_pin: data.pin,
          idempotency_key: idempotencyKey
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Refund failed');
      toast.success(`Refund of ₱${refundTarget.amount.toFixed(2)} approved`);
      setShowRefundDialog(false);
      setRefundTarget(null);
      fetchDayStatus();
    } catch (err) {
      toast.error(err.message);
    }
    setRefundLoading(false);
  };

  // Receipt printing state
  const [showReceiptPrint, setShowReceiptPrint] = useState(false);
  const [printTicketId, setPrintTicketId] = useState(null);

  // Reload the active ticket + rebuild the cart (used after line-discount changes)
  const refreshCartFromTicket = async (ticketId) => {
    const r = await fetch(`${API_URL}/api/pos/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const fresh = await r.json();
    setCurrentTicket(fresh);
    setCart(fresh.items?.filter(i => !i.is_voided).map(i => ({
      id: i.menu_item_id || i.id, name: i.name, price: i.unit_price, quantity: i.quantity,
      notes: i.notes, orderItemId: i.id, discount_amount: i.discount_amount || 0,
    })) || []);
  };

  const openItemDiscount = (item) => {
    setItemDiscTarget(item);
    setItemDiscType('percent');
    setItemDiscValue('');
    setItemDiscPin('');
    setShowItemDiscount(true);
  };

  const applyBogo = async (item) => {
    if (!currentTicket?.id || !item?.orderItemId) { toast.error('Load an order first'); return; }
    if ((item.quantity || 0) < 2) { toast.error('Add at least 2 of this item for Buy 1 Get 1'); return; }
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/${currentTicket.id}/items/${item.orderItemId}/bogo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ buy_qty: 1, free_qty: 1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to apply promo');
      await refreshCartFromTicket(currentTicket.id);
      toast.success(`Buy 1 Get 1 applied — ${data.free_units} free`);
    } catch (err) { toast.error(err.message); }
  };

  const addCustomItem = async () => {
    if (dayStatus.is_locked) { toast.error('Cannot add items - Day is locked by Z-Reading'); return; }
    const name = customItem.name.trim();
    const price = Number(customItem.unit_price);
    if (!name) { toast.error('Item name is required'); return; }
    if (!(price > 0)) { toast.error('Enter a price greater than 0'); return; }
    try {
      // Ensure a ticket exists (same as addToCart for quick-service orders).
      let ticket = currentTicket;
      if (!ticket?.id) {
        const outletId = currentOutlet?.id || outlet?.id || selectedOutlet || localStorage.getItem('selectedOutletId');
        if (!outletId) { toast.error('No outlet selected'); return; }
        const cr = await fetch(`${API_URL}/api/pos/tickets`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ outlet_id: outletId, table_id: selectedTable?.id || null, guest_count: 1,
                                 service_type: selectedTable?.id ? 'dine_in' : serviceType })
        });
        if (!cr.ok) { const e = await cr.json().catch(() => ({})); throw new Error(e.detail || 'Failed to start order'); }
        ticket = await cr.json();
        setCurrentTicket(ticket);
      }
      const res = await fetch(`${API_URL}/api/pos/tickets/${ticket.id}/custom-item`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name, unit_price: price, quantity: Number(customItem.quantity) || 1,
          tax_type: customItem.tax_type, manager_pin: customItem.manager_pin.trim() || null,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add custom item');
      // Show it on the cart immediately (optimistic), then reconcile with the
      // server so it can never be "missing" from checkout due to a refresh race.
      const qty = Number(customItem.quantity) || 1;
      setCart(prev => [...prev, {
        id: data.item_id, name, price, quantity: qty,
        notes: null, orderItemId: data.item_id, discount_amount: 0,
      }]);
      setShowCustomItem(false);
      setCustomItem({ name: '', unit_price: '', quantity: 1, tax_type: 'VAT', manager_pin: '' });
      toast.success(`Added "${name}"`);
      await refreshCartFromTicket(ticket.id);
    } catch (err) { toast.error(err.message); }
  };

  const applyItemDiscount = async () => {
    if (!currentTicket?.id || !itemDiscTarget?.orderItemId) { toast.error('Load an order first'); return; }
    const val = Number(itemDiscValue || 0);
    if (val <= 0) { toast.error('Enter a discount value'); return; }
    if (!itemDiscPin.trim()) { toast.error('Manager PIN is required'); return; }
    setItemDiscBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/${currentTicket.id}/items/${itemDiscTarget.orderItemId}/discount`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ discount_type: itemDiscType, discount_value: val, manager_pin: itemDiscPin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to apply discount');
      await refreshCartFromTicket(currentTicket.id);
      toast.success(`Discount applied to ${itemDiscTarget.name}`);
      setShowItemDiscount(false);
    } catch (err) { toast.error(err.message); }
    setItemDiscBusy(false);
  };

  const clearItemDiscount = async (item) => {
    if (!currentTicket?.id || !item?.orderItemId) return;
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/${currentTicket.id}/items/${item.orderItemId}/discount`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      await refreshCartFromTicket(currentTicket.id);
      toast.success('Discount removed');
    } catch (err) { toast.error(err.message); }
  };

  // Print receipt handler
  const handlePrintReceipt = (ticketId) => {
    setPrintTicketId(ticketId);
    setShowReceiptPrint(true);
  };

  // Fetch current ticket after updates
  const fetchCurrentTicket = useCallback(async (ticketId) => {
    if (!ticketId || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const ticket = await res.json();
        setCurrentTicket(ticket);
        // Sync cart with ticket items
        setCart(ticket.items?.filter(i => !i.is_voided).map(i => ({
          id: i.menu_item_id || i.id, 
          name: i.name, 
          price: i.unit_price, 
          quantity: i.quantity, 
          notes: i.notes, 
          orderItemId: i.id, discount_amount: i.discount_amount || 0
        })) || []);
        toast.success('Order sent to kitchen!');
      }
    } catch (err) {
      console.error('Failed to fetch ticket:', err);
    }
  }, [token]);

  // Apply discount handler
  const getDiscountPreview = () => {
    // Use the live cart-derived figures (always fresh) rather than the ticket
    // snapshot, which can lag (its service_charge was 0 → preview showed 320).
    const baseSubtotal = Number(subtotal || currentTicket?.subtotal || 0);
    const baseTip = Number(currentTicket?.tip_amount ?? 0);
    // The discount comes off the subtotal, THEN the service charge is taken on
    // what is left -- the same order as the server
    // (services/tax_service.compute_ticket_tax). Recomputing the charge here
    // rather than reusing the ticket's pre-discount figure is the whole point:
    // the charge moves when the discount does.
    //
    // Subtotal is already VAT-inclusive, so no tax is added on top (doing that
    // once showed 386.29 on a 352.00 order), and currentTicket.total is not
    // trusted because it can lag (it showed 320 instead of 352).
    const scRate = Number(serviceChargeRate || 0) / 100;
    const afterDiscount = (net) => {
      const n = Math.max(0, net);
      return n + n * scRate + baseTip;
    };
    const rawValue = Number(discountValue || 0);

    if (isStatutory) {
      // Indicative only: the server recomputes every figure from the outlet's
      // own VAT rate and ALL live claims on the ticket. This assumes the whole
      // bill for one beneficiary, which is the common single-diner case.
      const rate = Number(statutoryEntry.rate_percent || 0) / 100;
      const vatExemptSales = statutoryEntry.vat_exempt ? baseSubtotal / 1.12 : baseSubtotal;
      const vatRemoved = baseSubtotal - vatExemptSales;
      const discountAmount = vatExemptSales * rate;
      const newTotal = afterDiscount(vatExemptSales - discountAmount);

      return {
        discountAmount,
        vatExemptSales,
        vatRemoved,
        newTotal,
        label: `${statutoryEntry.label} ${statutoryEntry.rate_percent}%`
      };
    }

    // Percentage-based: Manual Percentage, or a Custom discount set to '%'
    if (discountType === 'MANUAL_PERCENT' || (isCustomDiscount && customDiscMode === 'percent')) {
      const discountAmount = baseSubtotal * (rawValue / 100);
      return {
        discountAmount,
        vatExemptSales: 0,
        vatRemoved: 0,
        newTotal: afterDiscount(baseSubtotal - discountAmount),
        label: isCustomDiscount ? `${customDiscName || 'Custom'} ${rawValue}%` : `Manual ${rawValue}%`
      };
    }

    const discountAmount = rawValue;
    return {
      discountAmount,
      vatExemptSales: 0,
      vatRemoved: 0,
      newTotal: afterDiscount(baseSubtotal - discountAmount),
      label: discountType.replace('_', ' ')
    };
  };

  const handleApplyDiscount = async () => {
    if (!currentTicket?.id) {
      toast.error('Create or load a ticket before applying a BIR discount');
      return;
    }

    const preview = getDiscountPreview();

    if (!discountType) {
      toast.error('Select a discount type');
      return;
    }

    if (isStatutory && (!discountBeneficiaryName.trim() || !discountBeneficiaryId.trim())) {
      toast.error(`Beneficiary name and ${statutoryEntry.id_label} are required for the ${statutoryEntry.label} discount`);
      return;
    }

    if (isStatutory) {
      if (discountBasis === 'items' && !discountEligibleIds.length) {
        toast.error("Select the item(s) the beneficiary consumed");
        return;
      }
      if (discountBasis === 'share' && !(Number(discountGroupSize) > 1)) {
        toast.error('Enter the party size (2 or more) to split the bill per head');
        return;
      }
    }

    if (!isStatutory && preview.discountAmount <= 0) {
      toast.error('Discount value is required');
      return;
    }

    if (preview.newTotal < 0) {
      toast.error('Discount cannot exceed sale amount');
      return;
    }

    if (isCustomDiscount && !customDiscName.trim()) {
      toast.error('Enter a name for the custom discount');
      return;
    }

    const isCustomPercent = isCustomDiscount && customDiscMode === 'percent';
    const isCustomAmount = isCustomDiscount && customDiscMode === 'amount';
    const apiDiscountType = isCustomDiscount
      ? 'CUSTOM'
      : (discountType === 'MANUAL_PERCENT' || discountType === 'MANUAL_AMOUNT' ? 'MANUAL' : discountType);

    const body = {
      discount_type: apiDiscountType,
      custom_label: isCustomDiscount ? customDiscName.trim() : null,
      custom_mode: isCustomDiscount ? customDiscMode : null,
      discount_rate:
        discountType === 'MANUAL_PERCENT' || isCustomPercent
          ? Number(discountValue || 0)
          : (isStatutory ? statutoryEntry.rate_percent : null),
      discount_amount:
        discountType === 'MANUAL_AMOUNT' || isCustomAmount || ['EMPLOYEE', 'PROMO', 'LOYALTY'].includes(discountType)
          ? Number(discountValue || 0)
          : null,
      beneficiary_name: discountBeneficiaryName.trim() || null,
      beneficiary_id_number: discountBeneficiaryId.trim() || null,
      reference_number: discountReference.trim() || null,
      reason: discountReason.trim() || null,
      manager_pin: discountManagerPin.trim() || null,
      // Only the beneficiary's own consumption is eligible on a group bill.
      eligible_item_ids:
        isStatutory && discountBasis === 'items' && discountEligibleIds.length
          ? discountEligibleIds
          : null,
      group_size:
        isStatutory && discountBasis === 'share' && Number(discountGroupSize) > 1
          ? Number(discountGroupSize)
          : null,
    };

    setDiscountLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/bir/tickets/${currentTicket.id}/discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to apply BIR discount');
      }

      setAppliedDiscount({
        type: apiDiscountType,
        value: data.discount_amount,
        label: `${apiDiscountType} Discount`,
        backend: data,
      });

      setShowDiscountDialog(false);
      toast.success(`${isCustomDiscount ? customDiscName.trim() : apiDiscountType} discount applied`);

      if (isCustomDiscount) loadCustomDiscounts();  // so the new custom appears next time

      if (currentTicket?.id) {
        await fetchCurrentTicket(currentTicket.id);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to apply BIR discount');
    }

    setDiscountLoading(false);
  };

  const clearDiscount = () => {
    setAppliedDiscount(null);
    setDiscountType('MANUAL_PERCENT');
    setDiscountValue('');
    setDiscountBeneficiaryName('');
    setDiscountBeneficiaryId('');
    setDiscountReference('');
    setDiscountReason('');
    setDiscountManagerPin('');
    setDiscountBasis('items');
    setDiscountEligibleIds([]);
    setDiscountGroupSize('');
    toast.info('Discount form cleared. Existing backend discount is not voided.');
  };

  // Search customers handler
  const handleSearchCustomers = async () => {
    if (!customerSearch.trim()) {
      setCustomerResults([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/customers?search=${encodeURIComponent(customerSearch)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomerResults(data.customers || []);
      }
    } catch (err) {
      console.error('Failed to search customers:', err);
    }
    setSearchingCustomers(false);
  };

  // Assign customer to ticket
  const handleAssignCustomer = async (customer) => {
    if (!currentTicket?.id) {
      toast.error('Please create a ticket first by selecting a table');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/${currentTicket.id}/assign-customer`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ customer_id: customer.id })
      });
      if (res.ok) {
        setSelectedCustomer(customer);
        setShowCustomerDialog(false);
        toast.success(`Assigned to ${customer.name || customer.phone}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to assign customer');
      }
    } catch (err) {
      console.error('Failed to assign customer:', err);
      toast.error('Failed to assign customer');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground" data-testid="pos-screen">
      {/* Day Locked Banner */}
      {dayStatus.is_locked && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-center gap-2" data-testid="day-locked-banner">
          <Lock className="w-4 h-4 text-red-600" />
          <span className="text-red-700 text-sm font-medium">
            Day is LOCKED by Z-Reading ({dayStatus.business_date}) - No transactions allowed
          </span>
        </div>
      )}

      {/* Header */}
      <header className="h-14 bg-card border-b border-border flex items-center px-2 lg:px-4 gap-2 lg:gap-4">
        {/* Navigation Menu Button */}
        <div className="flex items-center gap-1 lg:gap-2 shrink-0">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowNavMenu(!showNavMenu)}
            className="text-muted-foreground hover:text-foreground"
            data-testid="nav-menu-btn"
          >
            <LayoutDashboard className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setViewMode(viewMode === 'pos' ? 'tables' : 'pos')}
                  className="text-secondary" data-testid="toggle-view-btn">
            {viewMode === 'pos' ? <Table2 className="w-5 h-5" /> : <LayoutGrid className="w-5 h-5" />}
          </Button>
          <span className="hidden sm:inline-flex"><BrandWordmark className="text-xl text-foreground" accentClassName="text-secondary" /></span>
          <span className="text-muted-foreground hidden xl:inline">/</span>
          <span className="text-lg font-semibold whitespace-nowrap hidden xl:inline">{outlet?.name || 'POS'}</span>
        </div>
        
        <div className="hidden md:block flex-1 max-w-md min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search menu..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                   className="pl-10 bg-muted border-border" data-testid="menu-search" />
          </div>
        </div>

        <div className="flex-1 md:hidden" />
        {/* Cash Drawer Widget */}
        <div className="flex items-center gap-2 px-2 lg:px-3 py-1.5 bg-muted rounded-lg shrink-0 whitespace-nowrap" data-testid="cash-drawer-widget">
          <DollarSign className="w-4 h-4 text-secondary" />
          <div className="text-sm">
            <span className="text-muted-foreground hidden lg:inline">Cash: </span>
            <span className="font-semibold text-secondary">₱{cashBalance.toFixed(2)}</span>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hidden sm:inline-flex" onClick={fetchDayStatus} data-testid="refresh-cash-btn">
            <RefreshCw className={`w-3 h-3 ${loadingDayStatus ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Sync Status Indicator */}
        <div className="hidden sm:block"><SyncStatusIndicator /></div>

        <div className="flex items-center gap-1 lg:gap-2 shrink-0">
          {selectedTable && (
            <span className="px-3 py-1 bg-secondary rounded text-sm" data-testid="selected-table">
              {selectedTable.name}
            </span>
          )}
          
          {shift ? (
            <Button variant="outline" size="sm" onClick={() => setShowShiftClose(true)} data-testid="close-shift-btn" aria-label="Close shift">
              <Clock className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline whitespace-nowrap">Close Shift</span>
            </Button>
          ) : (
            <Button variant="default" size="sm" onClick={() => setShowShiftModal(true)} 
                    className="bg-secondary hover:bg-secondary/90 whitespace-nowrap" data-testid="open-shift-btn"
                    disabled={dayStatus.is_locked}>
              Open Shift
            </Button>
          )}

          {/* Z-Reading lives HERE, not in the Close Shift dialog: the endpoint
              refuses while any shift is open, so offering it there could only
              ever 400. It appears once every shift is closed. */}
          {!shift && !dayStatus.is_locked && (
            <Button variant="outline" size="sm" onClick={openZReadingDialog}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="z-reading-btn">
              <Lock className="w-4 h-4 xl:mr-1" /> <span className="hidden xl:inline">Z-Reading</span>
            </Button>
          )}
          
          {/* User Menu with Logout */}
          <Button variant="ghost" size="sm" onClick={openCustomerScreen} className="text-muted-foreground hidden md:inline-flex" title="Customer screen" aria-label="Customer screen" data-testid="customer-screen-btn">
            <Monitor className="w-5 h-5" />
          </Button>
          <UserMenu compact={true} />
        </div>
      </header>

      {/* Navigation Sidebar Drawer */}
      {showNavMenu && (
        <POSNavDrawer 
          isOwner={isOwner} 
          isManager={isManager} 
          onClose={() => setShowNavMenu(false)} 
        />
      )}

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {viewMode === 'tables' ? (
          <TableMapPage 
            outletId={selectedOutlet} 
            onTableSelect={handleTableSelect} 
            dayLocked={dayStatus.is_locked}
          />
        ) : (
          <>
            {/* Categories Sidebar */}
            <aside className="w-full md:w-[72px] lg:w-20 shrink-0 bg-card border-b md:border-b-0 md:border-r border-border flex flex-row md:flex-col overflow-x-auto md:overflow-x-visible no-scrollbar">
              <div className="flex flex-row md:flex-col flex-1 md:overflow-y-auto">
                {categories.map(cat => (
                  <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
                          className={`md:w-full shrink-0 px-4 md:p-3 py-3 min-h-[48px] md:min-h-[52px] whitespace-nowrap md:whitespace-normal flex flex-col items-center justify-center gap-1 transition-colors ${
                            selectedCategory === cat.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-muted'
                          }`} data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <span className="text-xs text-center leading-tight">{cat.name}</span>
                  </button>
                ))}
              </div>
            </aside>

            {/* Menu Items Grid */}
            <main className="flex-1 min-w-0 p-3 lg:p-4 pb-24 md:pb-3 lg:pb-4 overflow-auto">
              <div className="mb-3 flex flex-wrap gap-2 items-center">
                <div className="relative basis-full md:hidden">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search menu..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 bg-muted border-border h-11" data-testid="menu-search-phone" />
                </div>
                <input
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={handleBarcodeScan}
                  placeholder="Scan barcode and press Enter…"
                  className="hidden sm:block basis-full lg:basis-0 flex-1 min-w-0 h-10 px-3 rounded-md bg-card border border-border text-foreground text-sm"
                  data-testid="pos-barcode-input"
                />
                {!selectedTable?.id && !currentTicket?.id && (
                  <div className="flex shrink-0 rounded-md border border-border overflow-hidden" data-testid="pos-service-type">
                    {[['dine_in', 'Dine-in'], ['take_out', 'Take-out'], ['delivery', 'Delivery']].map(([v, label]) => (
                      <button key={v} onClick={() => setServiceType(v)}
                              className={`px-3 h-11 lg:h-10 text-xs whitespace-nowrap ${serviceType === v ? 'bg-secondary text-white' : 'bg-card text-muted-foreground hover:bg-muted'}`}
                              data-testid={`service-${v}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <Button variant="outline" className="h-11 lg:h-10 shrink-0 border-border whitespace-nowrap" onClick={() => setShowCustomItem(true)}
                        disabled={dayStatus.is_locked} data-testid="add-custom-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Custom
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {filteredItems.map(item => (
                  <button key={item.id} onClick={() => addToCart(item)}
                          disabled={dayStatus.is_locked}
                          className={`bg-card border border-border rounded-lg p-3 lg:p-4 text-left transition-colors active:scale-[0.98] ${
                            dayStatus.is_locked ? 'opacity-50 cursor-not-allowed' : 'hover:border-secondary'
                          }`}
                          data-testid={`menu-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="aspect-square bg-muted rounded mb-2 flex items-center justify-center overflow-hidden">
                      {item.image_url ? (
                        <img
                          src={getImageUrl(item.image_url)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img src="/bigmummy-logo.png" alt="" className="w-3/5 opacity-30" />
                      )}
                    </div>
                    <h3 className="font-medium text-sm leading-snug line-clamp-2 min-h-[2.5rem]">{item.name}</h3>
                    <p className="text-secondary font-bold">₱{item.price.toFixed(2)}</p>
                  </button>
                ))}
              </div>
            </main>

            <button type="button" onClick={() => setShowCart(true)}
                    className="md:hidden fixed bottom-3 left-3 right-3 z-30 h-14 rounded-xl bg-secondary text-white shadow-lg flex items-center justify-between px-4"
                    data-testid="view-order-btn">
              <span className="font-semibold">View order · {cart.reduce((n, i) => n + (i.quantity || 0), 0)} item(s)</span>
              <span className="font-bold">₱{Number(currentTicket?.total ?? total).toFixed(2)}</span>
            </button>
            {/* Cart Panel */}
            <aside className={`${showCart ? 'translate-y-0' : 'translate-y-full'} fixed inset-0 z-40 md:static md:translate-y-0 md:z-auto transition-transform duration-200 w-full md:w-72 lg:w-80 shrink-0 bg-card md:border-l border-border flex flex-col`}>
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Current Order
                  {currentTicket && <span className="text-xs text-muted-foreground">#{currentTicket.ticket_number}</span>}
                </h2>
                <button type="button" onClick={() => setShowCart(false)} className="md:hidden px-3 h-10 rounded-md border border-border text-sm" data-testid="close-order-btn">‹ Menu</button>
              </div>

              <ScrollArea className="flex-1 p-4">
                {cart.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No items in order</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cart.map(item => (
                      <div key={item.id} className="bg-muted rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2 gap-2 flex-wrap">
                          <span className="font-medium text-sm">{item.name}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openItemDiscount(item)}
                                    className="text-secondary hover:opacity-80 text-xs flex items-center gap-0.5 px-1.5 py-1.5"
                                    disabled={dayStatus.is_locked || !item.orderItemId}
                                    title="Discount this item"
                                    data-testid={`discount-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}>
                              <Percent className="w-3 h-3" /> Discount
                            </button>
                            <button onClick={() => applyBogo(item)}
                                    className="text-amber-500 hover:opacity-80 text-xs px-1.5 py-1.5"
                                    disabled={dayStatus.is_locked || !item.orderItemId || (item.quantity || 0) < 2}
                                    title="Buy 1 Get 1 free"
                                    data-testid={`bogo-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}>
                              B1G1
                            </button>
                            <button onClick={() => handleVoidItem(item)}
                                    className="text-red-600 hover:text-red-700 text-xs px-1.5 py-1.5"
                                    disabled={dayStatus.is_locked}
                                    data-testid={`void-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}>
                              Void
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQuantity(item.id, -1)}
                                    className="w-10 h-10 rounded-md bg-background border border-border flex items-center justify-center"
                                    disabled={dayStatus.is_locked}>
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, 1)}
                                    className="w-10 h-10 rounded-md bg-background border border-border flex items-center justify-center"
                                    disabled={dayStatus.is_locked}>
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          {item.discount_amount > 0 ? (
                            <span className="text-right leading-tight">
                              <span className="text-muted-foreground line-through text-xs mr-1">₱{(item.price * item.quantity).toFixed(2)}</span>
                              <span className="text-secondary">₱{(item.price * item.quantity - item.discount_amount).toFixed(2)}</span>
                              <button onClick={() => clearItemDiscount(item)} className="block text-[10px] text-green-600 hover:text-red-600 ml-auto"
                                      data-testid={`clear-item-discount-${item.name.toLowerCase().replace(/\s/g, '-')}`}>
                                −₱{item.discount_amount.toFixed(2)} ✕
                              </button>
                            </span>
                          ) : (
                            <span className="text-secondary">₱{(item.price * item.quantity).toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Totals */}
              <div className="p-4 border-t border-border space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₱{subtotal.toFixed(2)}</span>
                </div>
                {appliedDiscount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 flex items-center gap-1">
                      Discount ({appliedDiscount.label || appliedDiscount.type})
                      <button onClick={clearDiscount} className="text-red-600 hover:text-red-700 text-xs ml-1"
                              data-testid="clear-discount-btn">
                        ✕
                      </button>
                    </span>
                    <span className="text-green-600">-₱{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Charge ({serviceChargeRate}%)</span>
                  <span>₱{serviceCharge.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT ({vatRate}% incl.)</span>
                  <span>₱{vatAmount.toFixed(2)}</span>
                </div>
                {selectedCustomer && (
                  <div className="flex justify-between text-sm border-t border-border pt-2">
                    <span className="text-secondary">Customer</span>
                    <span className="text-secondary">{selectedCustomer.name || selectedCustomer.phone}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="text-secondary">₱{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-border grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" disabled={cart.length === 0 || dayStatus.is_locked}
                        onClick={() => { loadCustomDiscounts(); loadStatutoryTypes(); setShowDiscountDialog(true); }}
                        className={appliedDiscount ? 'border-green-300 text-green-600' : ''}
                        data-testid="discount-btn">
                  <Percent className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" 
                        onClick={() => setShowCustomerDialog(true)}
                        className={selectedCustomer ? 'border-secondary text-secondary' : ''}
                        data-testid="customer-btn">
                  <User className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" 
                        onClick={() => toast.info('Split bill feature coming soon')}
                        data-testid="split-btn">
                  <Users className="w-4 h-4" />
                </Button>
              </div>

              {/* Send to Kitchen */}
              <div className="px-4 pb-2">
                <SendToKitchenButton 
                  ticketId={currentTicket?.id}
                  disabled={cart.length === 0 || !currentTicket?.id}
                  onSent={() => fetchCurrentTicket(currentTicket.id)}
                />
              </div>

              {/* Cash Movement Buttons */}
              <div className="p-4 border-t border-border grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCashIn(true)}
                        disabled={!shift || dayStatus.is_locked}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        data-testid="cash-in-btn">
                  <ArrowDownToLine className="w-4 h-4 mr-1" />
                  In
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowCashOut(true)}
                        disabled={!shift || dayStatus.is_locked}
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        data-testid="cash-out-btn">
                  <ArrowUpFromLine className="w-4 h-4 mr-1" />
                  Out
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowDeposit(true)}
                        disabled={!shift || dayStatus.is_locked}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        data-testid="deposit-btn">
                  <Banknote className="w-4 h-4 mr-1" />
                  Dep
                </Button>
              </div>

              <div className="p-4">
                <Button className="w-full h-12 bg-secondary hover:bg-secondary/90 text-lg" 
                        disabled={cart.length === 0 || !shift || dayStatus.is_locked || !currentTicket?.id}
                        onClick={() => setShowPayment(true)} data-testid="pay-btn">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay ₱{Number(currentTicket?.total ?? total).toFixed(2)}
                </Button>
              </div>
            </aside>
          </>
        )}
      </div>

      {/* Modals */}
      <ShiftModal open={showShiftModal} onClose={() => setShowShiftModal(false)} onOpen={handleOpenShift} />
      
      <PaymentModal open={showPayment} onClose={() => setShowPayment(false)} total={Number(currentTicket?.total ?? total)}
                    cart={cart} outletId={selectedOutlet} tableId={selectedTable?.id} shift={shift}
                    onComplete={handlePaymentComplete} currentTicket={currentTicket} />
      
      <VoidModal open={showVoid} onClose={() => { setShowVoid(false); setVoidTarget(null); }}
                 target={voidTarget} outletId={selectedOutlet} onComplete={(voidLogId) => {
                   setShowVoid(false);
                   setVoidTarget(null);
                   if (voidTarget?.type === 'item') {
                     removeFromCart(voidTarget.item.id);
                   }
                   fetchDayStatus();
                   // BIR: a void must produce a document that can be printed and kept.
                   if (voidLogId) setVoidSlipId(voidLogId);
                 }} />

      <VoidSlipPrintDialog open={!!voidSlipId} voidLogId={voidSlipId} onClose={() => setVoidSlipId(null)} />

      {/* Custom / open item */}
      <Dialog open={showCustomItem} onOpenChange={setShowCustomItem}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Item</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Ring up an item not on the menu, with its BIR tax treatment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm text-muted-foreground">Item name</label>
              <Input value={customItem.name} onChange={e => setCustomItem({ ...customItem, name: e.target.value })}
                     placeholder="e.g. Special Order" className="bg-muted border-border mt-1" data-testid="custom-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground">Price (₱)</label>
                <Input type="number" min={0} step="any" value={customItem.unit_price} onFocus={e => e.target.select()}
                       onChange={e => setCustomItem({ ...customItem, unit_price: e.target.value })}
                       className="bg-muted border-border mt-1" data-testid="custom-price" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Qty</label>
                <Input type="number" min={1} value={customItem.quantity} onFocus={e => e.target.select()}
                       onChange={e => setCustomItem({ ...customItem, quantity: e.target.value })}
                       className="bg-muted border-border mt-1" data-testid="custom-qty" />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Tax treatment (BIR)</label>
              <select value={customItem.tax_type} onChange={e => setCustomItem({ ...customItem, tax_type: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded-md bg-muted border border-border text-foreground" data-testid="custom-tax">
                <option value="VAT">VAT (12%, inclusive)</option>
                <option value="VAT_EXEMPT">VAT-Exempt</option>
                <option value="ZERO_RATED">Zero-Rated (0%)</option>
                <option value="NO_TAX">No Tax</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Manager PIN <span className="opacity-60">(if not a manager)</span></label>
              <Input type="password" value={customItem.manager_pin} onChange={e => setCustomItem({ ...customItem, manager_pin: e.target.value })}
                     placeholder="••••" className="bg-muted border-border mt-1" data-testid="custom-pin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomItem(false)}>Cancel</Button>
            <Button className="bg-secondary hover:bg-secondary/90" onClick={addCustomItem} data-testid="custom-add-btn">Add to Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Movement Dialogs */}
      <CashMovementDialog 
        open={showCashIn} 
        onClose={() => setShowCashIn(false)}
        onConfirm={handleCashIn}
        type="in"
        loading={cashMovementLoading}
      />
      
      <CashMovementDialog 
        open={showCashOut} 
        onClose={() => setShowCashOut(false)}
        onConfirm={handleCashOut}
        type="out"
        loading={cashMovementLoading}
      />
      
      <CashMovementDialog 
        open={showDeposit} 
        onClose={() => setShowDeposit(false)}
        onConfirm={handleDeposit}
        type="deposit"
        loading={cashMovementLoading}
      />

      {/* Z-Reading Dialog */}
      <ZReadingDialog
        open={showZReading}
        onClose={() => { setShowZReading(false); setZReadingResult(null); }}
        onConfirm={handleZReading}
        shiftSummary={shiftSummary}
        loading={zReadingLoading}
        zReadingResult={zReadingResult}
      />

      {/* Guest Count Prompt Dialog */}
      <Dialog open={showGuestCount} onOpenChange={() => setShowGuestCount(false)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">
              How many guests?
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="flex items-center justify-center gap-4">
              <Button
                size="lg"
                variant="outline"
                className="h-14 w-14 text-2xl"
                onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
              >
                -
              </Button>
              <div className="text-5xl font-bold text-secondary w-20 text-center">
                {guestCount}
              </div>
              <Button
                size="lg"
                variant="outline"
                className="h-14 w-14 text-2xl"
                onClick={() => setGuestCount(Math.min(20, guestCount + 1))}
              >
                +
              </Button>
            </div>
            <div className="flex justify-center gap-2 mt-6">
              {[1, 2, 4, 6, 8].map(n => (
                <Button
                  key={n}
                  size="sm"
                  variant={guestCount === n ? 'default' : 'outline'}
                  onClick={() => setGuestCount(n)}
                  className={guestCount === n ? 'bg-secondary' : ''}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGuestCount(false); setPendingTable(null); }}>
              Cancel
            </Button>
            <Button onClick={confirmSeating} className="bg-secondary hover:bg-secondary/90" data-testid="confirm-seating-btn">
              Seat {guestCount} Guest{guestCount > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Manager PIN Dialog */}
      <ManagerPinDialog
        open={showRefundDialog}
        onClose={() => { setShowRefundDialog(false); setRefundTarget(null); }}
        onConfirm={handleRefund}
        title="Refund Approval"
        description={`Approve refund of ₱${refundTarget?.amount?.toFixed(2) || '0.00'}. This requires manager authorization.`}
        requireReason={true}
        loading={refundLoading}
      />

      {/* Close Shift Modal with Enhanced UI */}
      <Dialog open={showShiftClose} onOpenChange={setShowShiftClose}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Close Shift</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Review your shift summary and enter counted cash to close the shift.
            </DialogDescription>
          </DialogHeader>
          <CloseShiftForm 
            shift={shift} 
            outletId={selectedOutlet} 
            dayStatus={dayStatus}
            onClose={() => {
              setShowShiftClose(false);
              setShift(null);
              fetchData();
              fetchDayStatus();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Receipt Print Dialog */}
      <ReceiptPrintDialog
        open={showReceiptPrint}
        onClose={() => { setShowReceiptPrint(false); setPrintTicketId(null); }}
        ticketId={printTicketId}
      />

      {/* Per-item (line) discount dialog */}
      <Dialog open={showItemDiscount} onOpenChange={setShowItemDiscount}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="item-discount-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Percent className="w-5 h-5 text-secondary" /> Discount item</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {itemDiscTarget ? `${itemDiscTarget.name} — ₱${(itemDiscTarget.price * itemDiscTarget.quantity).toFixed(2)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={itemDiscType === 'percent' ? 'default' : 'outline'}
                      className={itemDiscType === 'percent' ? 'bg-secondary' : 'border-border'}
                      onClick={() => setItemDiscType('percent')} data-testid="item-disc-percent">Percent (%)</Button>
              <Button type="button" variant={itemDiscType === 'amount' ? 'default' : 'outline'}
                      className={itemDiscType === 'amount' ? 'bg-secondary' : 'border-border'}
                      onClick={() => setItemDiscType('amount')} data-testid="item-disc-amount">Amount (₱)</Button>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{itemDiscType === 'percent' ? 'Percent off (0–100)' : 'Amount off (₱)'}</Label>
              <Input type="number" value={itemDiscValue} onChange={e => setItemDiscValue(e.target.value)}
                     placeholder={itemDiscType === 'percent' ? 'e.g. 10' : 'e.g. 50'} min={0} className="mt-1 bg-muted border-border"
                     data-testid="item-disc-value" />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Manager PIN</Label>
              <Input type="password" value={itemDiscPin} onChange={e => setItemDiscPin(e.target.value)}
                     placeholder="Required to authorise" className="mt-1 bg-muted border-border" data-testid="item-disc-pin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDiscount(false)} className="border-border">Cancel</Button>
            <Button onClick={applyItemDiscount} disabled={itemDiscBusy} className="bg-secondary hover:bg-secondary/90" data-testid="apply-item-disc-btn">
              {itemDiscBusy ? 'Applying…' : 'Apply Discount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BIR Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Apply BIR Discount</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Discounts are applied through the backend and logged for BIR audit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground">Discount Type</label>
              <select
                value={discountType}
                onChange={(e) => {
                  const v = e.target.value;
                  setDiscountType(v);
                  if (v === 'CUSTOM_NEW') { setCustomDiscName(''); setCustomDiscMode('percent'); setDiscountValue(''); }
                  else if (v.startsWith('SAVED::')) {
                    const cd = customDiscounts[Number(v.split('::')[1])];
                    if (cd) { setCustomDiscName(cd.name); setCustomDiscMode(cd.mode || 'percent'); setDiscountValue(cd.value != null ? String(cd.value) : ''); }
                  }
                }}
                className="w-full mt-1 bg-muted border border-border rounded px-3 py-2 text-foreground"
              >
                <option value="MANUAL_PERCENT">Manual Percentage</option>
                <option value="MANUAL_AMOUNT">Manual Amount</option>
                {statutoryTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label} ({t.rate_percent}%{t.vat_exempt ? ', VAT exempt' : ''}) - {t.law}
                  </option>
                ))}
                <option value="EMPLOYEE">Employee</option>
                <option value="PROMO">Promo</option>
                <option value="LOYALTY">Loyalty</option>
                {customDiscounts.length > 0 && <option disabled>──────────</option>}
                {customDiscounts.map((cd, i) => (
                  <option key={cd.name} value={`SAVED::${i}`}>{cd.name}{cd.mode === 'percent' && cd.value ? ` (${cd.value}%)` : ''}</option>
                ))}
                <option value="CUSTOM_NEW">➕ Custom Discount…</option>
              </select>
            </div>

            {isCustomDiscount && (
              <div className="space-y-2">
                <div>
                  <label className="text-sm text-muted-foreground">Discount name</label>
                  <Input value={customDiscName} onChange={e => setCustomDiscName(e.target.value)}
                         placeholder="e.g. Athlete, Single Mother" className="bg-muted border-border mt-1"
                         disabled={discountType.startsWith('SAVED::')} data-testid="custom-disc-name" />
                </div>
                <div className="flex rounded-md border border-border overflow-hidden w-fit">
                  {[['percent', '%'], ['amount', '₱']].map(([m, lbl]) => (
                    <button key={m} type="button" onClick={() => setCustomDiscMode(m)}
                            className={`px-4 py-1 text-sm ${customDiscMode === m ? 'bg-secondary text-white' : 'bg-muted text-muted-foreground'}`}
                            data-testid={`custom-disc-mode-${m}`}>{lbl}</button>
                  ))}
                </div>
              </div>
            )}

            {!isStatutory && (
              <div>
                <label className="text-sm text-muted-foreground">
                  {(discountType === 'MANUAL_PERCENT' || (isCustomDiscount && customDiscMode === 'percent')) ? 'Discount Percentage' : 'Discount Amount'}
                </label>
                <Input
                  type="number"
                  value={discountValue}
                  onChange={e => setDiscountValue(e.target.value)}
                  className="bg-muted border-border mt-1"
                  placeholder={(discountType === 'MANUAL_PERCENT' || (isCustomDiscount && customDiscMode === 'percent')) ? '10' : '100.00'}
                />
              </div>
            )}

            {isStatutory && (
              <>
                <div>
                  <label className="text-sm text-muted-foreground">Beneficiary Name</label>
                  <Input
                    value={discountBeneficiaryName}
                    onChange={e => setDiscountBeneficiaryName(e.target.value)}
                    className="bg-muted border-border mt-1"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground">
                    {statutoryEntry ? statutoryEntry.id_label : 'ID Number'}
                  </label>
                  <Input
                    value={discountBeneficiaryId}
                    onChange={e => setDiscountBeneficiaryId(e.target.value)}
                    className="bg-muted border-border mt-1"
                    placeholder="Required"
                  />
                </div>

                {/* Group bill: only the beneficiary's own consumption is discountable */}
                <div className="border border-border rounded-md p-3 space-y-2">
                  <label className="text-sm text-foreground font-medium">What is the beneficiary entitled to?</label>
                  <div className="flex gap-2 text-xs">
                    {[['items', "Their item(s)"], ['share', 'Per-head share'], ['whole', 'Entire bill']].map(([v, label]) => (
                      <button key={v} type="button" onClick={() => setDiscountBasis(v)}
                              className={`px-2 py-1 rounded ${discountBasis === v ? 'bg-secondary text-white' : 'bg-muted text-muted-foreground'}`}
                              data-testid={`disc-basis-${v}`}>{label}</button>
                    ))}
                  </div>

                  {discountBasis === 'items' && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {cart.filter(i => i.orderItemId).map(i => (
                        <label key={i.orderItemId} className="flex items-center gap-2 text-sm text-foreground">
                          <input type="checkbox" checked={discountEligibleIds.includes(i.orderItemId)}
                                 onChange={() => setDiscountEligibleIds(prev => prev.includes(i.orderItemId)
                                   ? prev.filter(x => x !== i.orderItemId)
                                   : [...prev, i.orderItemId])}
                                 data-testid={`disc-item-${i.orderItemId}`} />
                          <span className="flex-1">{i.name} × {i.quantity}</span>
                          <span className="text-muted-foreground">₱{((i.price * i.quantity) - (i.discount_amount || 0)).toFixed(2)}</span>
                        </label>
                      ))}
                      {!cart.filter(i => i.orderItemId).length && (
                        <p className="text-xs text-muted-foreground">No saved items yet — add items to the order first.</p>
                      )}
                    </div>
                  )}

                  {discountBasis === 'share' && (
                    <div>
                      <label className="text-xs text-muted-foreground">Number of persons in the party</label>
                      <Input type="number" min={2} value={discountGroupSize}
                             onChange={e => setDiscountGroupSize(e.target.value)}
                             className="bg-muted border-border mt-1 w-32" placeholder="e.g. 5"
                             data-testid="disc-group-size" />
                      <p className="text-xs text-muted-foreground mt-1">Bill ÷ persons = the beneficiary's share. Use when food is shared and can't be itemised.</p>
                    </div>
                  )}

                  {discountBasis === 'whole' && (
                    <p className="text-xs text-amber-500">The whole bill will be treated as the beneficiary's. Only correct when dining alone.</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="text-sm text-muted-foreground">Reference Number</label>
              <Input
                value={discountReference}
                onChange={e => setDiscountReference(e.target.value)}
                className="bg-muted border-border mt-1"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Reason / Notes</label>
              <Input
                value={discountReason}
                onChange={e => setDiscountReason(e.target.value)}
                className="bg-muted border-border mt-1"
                placeholder="Required for manual approvals"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Manager PIN</label>
              <Input
                type="password"
                value={discountManagerPin}
                onChange={e => setDiscountManagerPin(e.target.value)}
                className="bg-muted border-border mt-1"
                placeholder="Optional unless policy requires"
              />
            </div>

            <div className="bg-background rounded-lg p-3 text-sm space-y-1">
              {(() => {
                const preview = getDiscountPreview();
                return (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-red-600">-₱{preview.discountAmount.toFixed(2)}</span>
                    </div>
                    {preview.vatRemoved > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">VAT Removed</span>
                        <span>₱{preview.vatRemoved.toFixed(2)}</span>
                      </div>
                    )}
                    {preview.vatExemptSales > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">VAT Exempt Sales</span>
                        <span>₱{preview.vatExemptSales.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t border-border pt-2 mt-2">
                      <span>New Total</span>
                      <span className="text-secondary">₱{preview.newTotal.toFixed(2)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountDialog(false)}>Cancel</Button>
            <Button onClick={handleApplyDiscount} disabled={discountLoading} className="bg-secondary hover:bg-secondary/90">
              {discountLoading ? 'Applying...' : 'Apply BIR Discount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Assignment Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Assign Customer</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Search for a customer to assign to this order
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input 
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                placeholder="Search by name or phone..."
                className="bg-muted border-border"
                onKeyDown={e => e.key === 'Enter' && handleSearchCustomers()}
                data-testid="customer-search-input"
              />
              <Button onClick={handleSearchCustomers} className="bg-secondary hover:bg-secondary/90"
                      disabled={searchingCustomers} data-testid="search-customer-btn">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            {selectedCustomer && (
              <div className="bg-secondary/10 border border-secondary rounded-lg p-3">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-secondary">{selectedCustomer.name || 'Guest'}</p>
                    <p className="text-sm text-muted-foreground">{selectedCustomer.phone}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)}
                          className="text-red-600 hover:text-red-700">
                    Remove
                  </Button>
                </div>
              </div>
            )}
            
            <ScrollArea className="h-48">
              {searchingCustomers ? (
                <div className="text-center text-muted-foreground py-4">Searching...</div>
              ) : customerResults.length > 0 ? (
                <div className="space-y-2">
                  {customerResults.map(customer => (
                    <button
                      key={customer.id}
                      onClick={() => handleAssignCustomer(customer)}
                      className="w-full p-3 bg-muted rounded-lg hover:bg-muted text-left transition-colors"
                      data-testid={`customer-result-${customer.id}`}
                    >
                      <p className="font-medium">{customer.name || 'Guest'}</p>
                      <p className="text-sm text-muted-foreground">{customer.phone} • {customer.email || 'No email'}</p>
                      {customer.loyalty_points > 0 && (
                        <p className="text-xs text-secondary mt-1">{customer.loyalty_points} loyalty points</p>
                      )}
                    </button>
                  ))}
                </div>
              ) : customerSearch ? (
                <div className="text-center text-muted-foreground py-4">No customers found</div>
              ) : (
                <div className="text-center text-muted-foreground py-4">Enter a name or phone to search</div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CloseShiftForm({ shift, outletId, dayStatus, onClose }) {
  const { token } = useAuth();
  const [countedCash, setCountedCash] = useState('');
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (shift && outletId) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cashier/shift-summary?outlet_id=${outletId}&shift_id=${shift.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        setSummary(data);
        if (data?.expected_cash) {
          setCountedCash(data.expected_cash.toFixed(2));
        }
      }).catch(console.error);
    }
  }, [shift, outletId, token]);

  const handleClose = async () => {
    if (!countedCash) {
      toast.error('Please enter counted cash');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pos/shifts/${shift.id}/close?counted_cash=${countedCash}&notes=${encodeURIComponent(notes || '')}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Shift closed. Variance: ₱${data.variance?.toFixed(2) || '0.00'}`);
        onClose();
      } else {
        toast.error(data.detail || 'Failed to close shift');
      }
    } catch (err) {
      toast.error('Failed to close shift');
    }
    setLoading(false);
  };

  const variance = summary?.expected_cash && countedCash 
    ? parseFloat(countedCash) - summary.expected_cash 
    : 0;

  return (
    <div className="space-y-4">
      {summary && (
        <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Starting Cash</span>
              <span>₱{summary.starting_cash?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cash Sales</span>
              <span className="text-green-600">+₱{summary.cash_sales?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cash In</span>
              <span className="text-green-600">+₱{summary.cash_in?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cash Out</span>
              <span className="text-red-600">-₱{summary.cash_out?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Deposits</span>
              <span className="text-blue-600">-₱{summary.deposits?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Refunds</span>
              <span className="text-red-600">-₱{summary.cash_refunds?.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex justify-between border-t border-border pt-2 mt-2">
            <span className="font-semibold">Expected Cash</span>
            <span className="font-bold text-secondary">₱{summary.expected_cash?.toFixed(2)}</span>
          </div>
        </div>
      )}
      
      <div>
        <label className="text-sm text-muted-foreground">Counted Cash</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₱</span>
          <Input type="number" value={countedCash} onChange={e => setCountedCash(e.target.value)}
                 placeholder="Enter counted cash" className="bg-muted border-border pl-8"
                 data-testid="close-shift-counted-cash" />
        </div>
        {countedCash && summary?.expected_cash && (
          <p className={`text-sm mt-1 ${variance === 0 ? 'text-green-600' : variance > 0 ? 'text-blue-600' : 'text-red-600'}`}
             data-testid="cash-variance-display">
            Variance: ₱{variance.toFixed(2)} {variance > 0 ? '(Over)' : variance < 0 ? '(Short)' : '(Balanced)'}
          </p>
        )}
      </div>
      
      <div>
        <label className="text-sm text-muted-foreground">Notes (optional)</label>
        <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..."
               className="bg-muted border-border" />
      </div>

      {/* Z-Reading Notice */}
      {!dayStatus?.is_locked && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-700">
              <p className="font-semibold">Remember to create Z-Reading</p>
              <p className="text-yellow-600 mt-1">
                Close this shift first. The Z-Reading button then appears in the top bar, and locks the business day.
              </p>
            </div>
          </div>
        </div>
      )}

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleClose} disabled={loading || dayStatus?.is_locked} 
                className="bg-secondary hover:bg-secondary/90" data-testid="confirm-close-shift-btn">
          {loading ? 'Processing...' : 'Close Shift'}
        </Button>
      </DialogFooter>
    </div>
  );
}
